import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useSettings, resolveImage } from "../lib/settings";
import { Nav, Footer } from "../components/Nav";
import { VideoPreview } from "../components/VideoPreview";
import { toast } from "sonner";
import { playClick } from "../lib/clickSound";

const HERO_DEFAULT = "https://images.unsplash.com/photo-1719947348862-a9840862d118?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHw0fHxmYXNoaW9uJTIwaWxsdXN0cmF0aW9uJTIwc2tldGNofGVufDB8fHx8MTc4NTUwNzk1MHww&ixlib=rb-4.1.0&q=85";
const G1_DEFAULT = "https://images.unsplash.com/photo-1557777586-f6682739fcf3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxmYXNoaW9uJTIwaWxsdXN0cmF0aW9uJTIwc2tldGNofGVufDB8fHx8MTc4NTUwNzk1MHww&ixlib=rb-4.1.0&q=85";
const G2_DEFAULT = "https://images.unsplash.com/photo-1586447795212-b8ea5253ac2c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHxmYXNoaW9uJTIwaWxsdXN0cmF0aW9uJTIwc2tldGNofGVufDB8fHx8MTc4NTUwNzk1MHww&ixlib=rb-4.1.0&q=85";
const TEXTURE = "https://images.unsplash.com/photo-1714636608872-048fc9231892?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxtaW5pbWFsJTIwbHV4dXJ5JTIwdGV4dHVyZSUyMGJlaWdlfGVufDB8fHx8MTc4NTUwNzk1MHww&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const { settings } = useSettings();
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
  }, []);

  if (!settings) return <div className="p-10">Loading…</div>;

  const learn = settings.course_what_you_learn || [];
  const heroImg = resolveImage(settings.hero_image_path, HERO_DEFAULT);
  const g1 = resolveImage(settings.gallery_1_path, G1_DEFAULT);
  const g2 = resolveImage(settings.gallery_2_path, G2_DEFAULT);

  return (
    <div className="App bg-[#FAFAFA]" data-testid="landing-page">
      <Nav />

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12 md:pb-16 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-6 order-2 md:order-1 fade-up">
          <p className="overline mb-8"><span className="gold-line mr-3" />An Editorial Studio Course</p>
          <h1 className="font-serif-display font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
            {settings.hero_headline_1}<br />
            <span className="font-serif-italic italic text-[#D4AF37]">{settings.hero_headline_2}</span> {settings.hero_headline_3?.split(" ")[0]}<br />
            {settings.hero_headline_3?.split(" ").slice(1).join(" ")}
          </h1>
          <p className="mt-10 text-[#3a3a3a] leading-relaxed max-w-md text-base">
            {settings.hero_subtitle}
          </p>
          <div className="mt-12 flex flex-wrap gap-4">
            <Link data-testid="hero-enroll-cta" to="/course" onClick={playClick} className="btn-editorial">Explore The Course</Link>
            <Link data-testid="hero-about-cta" to="/about" onClick={playClick} className="btn-editorial-outline">Meet Lata</Link>
          </div>
        </div>
        <div className="md:col-span-6 order-1 md:order-2 relative fade-up-d1">
          <div className="relative aspect-[3/4] overflow-hidden">
            <img src={heroImg} alt="Fashion illustration" className="w-full h-full object-cover" />
          </div>
          <div className="absolute -bottom-6 -left-6 md:-left-10 bg-[#FAFAFA] border border-black/10 px-6 py-4 hidden md:block">
            <p className="overline">Module 01 of 10</p>
            <p className="font-serif-italic italic text-lg mt-1">"{settings.hero_module_quote}"</p>
          </div>
        </div>
      </section>

      {/* SIGNATURE STATEMENT — under hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-8 md:pt-16 pb-16 md:pb-24 fade-up-d2">
        <div className="border-t border-black/10 pt-12 md:pt-16 grid grid-cols-1 md:grid-cols-12 gap-6 items-baseline">
          <div className="md:col-span-2">
            <p className="overline"><span className="gold-line mr-3" />Artist Note</p>
          </div>
          <div className="md:col-span-10">
            <p className="font-serif-display font-light text-3xl md:text-5xl leading-[1.15] tracking-tight">
              "I'm an <span className="font-serif-italic italic text-[#D4AF37]">Art</span> first, Artist later."
            </p>
          </div>
        </div>
      </section>

      {/* COURSE PREVIEW — Bunny Stream */}
      <VideoPreview
        variant="light"
        eyebrow="Meet Lata Illustrator"
        headline={<>Meet <span className="font-serif-italic italic text-[#D4AF37]">Lata</span><br />Illustrator.</>}
        body="Lata in her creative space, where ideas take shape, colours come alive, and every illustration begins."
      />

      {/* MARQUEE / VALUE PROPS */}
      <section className="border-y border-black/10 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            ["10", "Curated Modules"],
            ["24", "Illustrated Lessons"],
            ["3", "Months of Access"],
            ["01", "Signed Certificate"],
          ].map(([n, l]) => (
            <div key={l}>
              <p className="font-serif-display text-4xl">{n}</p>
              <p className="overline mt-2">{l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHAT YOU'LL LEARN */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <p className="overline mb-6"><span className="gold-line mr-3" />The Curriculum</p>
          <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05] font-light">
            A studio taught<br />like an editorial.
          </h2>
        </div>
        <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-6">
          {learn.map((item, i) => (
            <div key={i} className="flex gap-4 py-3 border-b border-black/10" data-testid={`landing-learn-${i}`}>
              <span className="font-serif-italic italic text-[#D4AF37] text-lg">{String(i + 1).padStart(2, '0')}</span>
              <p className="text-[#0A0A0A] leading-relaxed">{item}</p>
            </div>
          ))}
        </div>
      </section>

      {/* THE METHOD — USP */}
      <section className="bg-[#0A0A0A] text-[#FAFAFA]">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-4">
            <p className="overline mb-6" style={{color: "#D4AF37"}}><span className="gold-line mr-3" />The Method</p>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05] font-light">
              No filler.<br /><span className="font-serif-italic italic text-[#D4AF37]">Only</span> the craft.
            </h2>
          </div>
          <div className="md:col-span-8 space-y-6 text-[#E5E5E5] leading-relaxed">
            <p className="text-lg">
              Every course Lata tried felt padded. A forty-minute lesson with seven minutes of actual teaching — the rest lost to filler, warm-up talk, and repetition. It frustrated her as a student, and she promised her students would never feel the same.
            </p>
            <p className="text-lg">
              So this course is deliberately <span className="font-serif-italic italic text-[#D4AF37]">cut to the bone</span>. Point to point. Cut to cut. Every second earns its place — no time wasted, no lesson longer than it needs to be.
            </p>
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-white/10 mt-10">
              <div>
                <p className="font-serif-display text-3xl">24</p>
                <p className="overline mt-2" style={{color: "#D4AF37"}}>Precise Lessons</p>
              </div>
              <div>
                <p className="font-serif-display text-3xl">~2h</p>
                <p className="overline mt-2" style={{color: "#D4AF37"}}>Studio Video</p>
              </div>
              <div>
                <p className="font-serif-display text-3xl">0</p>
                <p className="overline mt-2" style={{color: "#D4AF37"}}>Minutes Wasted</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* IMAGE STRIP */}
      <section className="border-y border-black/10 bg-[#F2F0EB]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-4">
            <img src={g1} alt="Sketch" className="w-full aspect-[3/4] object-cover" />
          </div>
          <div className="md:col-span-4 md:pt-12">
            <p className="overline">Editorial Practice</p>
            <p className="font-serif-italic italic text-3xl mt-4 leading-snug">
              "{settings.editorial_quote}"
            </p>
          </div>
          <div className="md:col-span-4">
            <img src={g2} alt="Sketch" className="w-full aspect-[3/4] object-cover" />
          </div>
        </div>
      </section>

      {/* TESTIMONIALS PREVIEW */}
      {testimonials.length > 0 && (
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
          <div className="md:col-span-5">
            <p className="overline mb-6"><span className="gold-line mr-3" />Voices from the Studio</p>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05] font-light">
              What her<br />students say.
            </h2>
          </div>
          <div className="md:col-span-7 md:pt-12">
            <p className="text-[#3a3a3a] leading-relaxed max-w-lg">
              Illustrators, designers, and students across three continents — quietly transformed by a way of seeing.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.slice(0, 3).map((t, i) => (
            <div key={t.id || i} className="border border-black/10 bg-white p-8" data-testid={`landing-testimonial-${i}`}>
              <p className="font-serif-italic italic text-lg leading-snug">"{t.quote}"</p>
              <div className="mt-8 pt-6 border-t border-black/10">
                <p className="text-sm font-medium">{t.student_name}</p>
                <p className="overline mt-1">{t.role}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
      )}

      {/* BE THE FIRST — hidden for launch; will re-enable when students start reviewing */}
      {/* {testimonials.length === 0 && <BeTheFirstSection />} */}

      {/* CTA BAND */}
      <section
        className="relative border-y border-black/10 bg-cover bg-center"
        style={{ backgroundImage: `linear-gradient(rgba(250,250,250,0.85), rgba(250,250,250,0.85)), url(${TEXTURE})` }}
      >
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-24 md:py-32 text-center">
          <p className="overline">Begin the Journey</p>
          <h2 className="font-serif-display font-light text-4xl md:text-6xl mt-6 leading-tight">
            One Course. Three Months.<br />A Journey Through Art & Indian Fashion.
          </h2>
          <p className="mt-8 text-[#3a3a3a] max-w-lg mx-auto leading-relaxed">
            Enrol once — three months to study at your own pace, submit your final illustration, and receive a signed certificate from Lata.
          </p>
          <Link to="/enroll" data-testid="footer-enroll-cta" className="btn-editorial mt-12">Enroll Now</Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function BeTheFirstSection() {
  const [form, setForm] = useState({ student_name: "", role: "", quote: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/testimonials", form);
      setSubmitted(true);
      toast.success("Thank you. Lata will review your note.");
      setForm({ student_name: "", role: "", quote: "" });
    } catch { toast.error("Something went wrong"); }
    finally { setLoading(false); }
  };
  return (
    <section className="max-w-6xl mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="be-first-section">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
        <div className="md:col-span-5">
          <p className="overline mb-6"><span className="gold-line mr-3" />Voices from the Studio</p>
          <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05] font-light">
            Be the <span className="font-serif-italic italic text-[#D4AF37]">first</span> to write about this course.
          </h2>
          <p className="mt-8 text-[#3a3a3a] leading-relaxed max-w-md">
            The studio is quietly opening its doors. If you've taken the course — or you're taking it now — Lata would love to hear what it felt like, in your own words.
          </p>
        </div>
        <div className="md:col-span-7">
          {submitted ? (
            <div className="border border-black/10 bg-[#F2F0EB] p-10 text-center">
              <p className="overline text-[#D4AF37]"><span className="gold-line mr-3" />Received</p>
              <p className="font-serif-display text-2xl mt-4">Thank you.</p>
              <p className="text-sm text-[#3a3a3a] mt-4 max-w-sm mx-auto">Your note is with Lata. She reads every review personally, and will publish it here after approval.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="border border-black/10 bg-white p-8 md:p-10 space-y-6" data-testid="be-first-form">
              <div>
                <label className="overline">Your Name</label>
                <input required data-testid="review-name" value={form.student_name} onChange={(e) => setForm({ ...form, student_name: e.target.value })} className="w-full border-b border-black/30 bg-transparent py-3 focus:border-black outline-none" />
              </div>
              <div>
                <label className="overline">You Are (optional)</label>
                <input placeholder="e.g. Fashion student · Illustrator · Designer" data-testid="review-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full border-b border-black/30 bg-transparent py-3 focus:border-black outline-none" />
              </div>
              <div>
                <label className="overline">What did you feel about this course?</label>
                <textarea required rows={5} data-testid="review-quote" value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} className="w-full border border-black/15 bg-[#FAFAFA] p-4 text-sm outline-none focus:border-black mt-2" />
              </div>
              <button disabled={loading} data-testid="review-submit" className="btn-editorial">
                {loading ? "Sending…" : "Send to Lata"}
              </button>
              <p className="text-[10px] text-[#6B6B6B]">Your review will appear here after Lata's approval.</p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
